import { create } from 'zustand';
import { log } from '../utils/logger';
import {
  fetchFolders,
  createFolder as apiCreateFolder,
  renameFolder as apiRenameFolder,
  deleteFolder as apiDeleteFolder,
  saveGameToFolder,
  unsaveGame as apiUnsaveGame,
} from '../services/review-api';
import type { ReviewFolder } from '../types/review';

/**
 * Review library: folders and saved-game counts.
 *
 * Zustand rather than local state because this crosses screens: the counts are
 * MUTATED in the replay screen (saving a game) and READ in the profile's games
 * tab. ProfilePage doesn't unmount when the replay opens — it early-returns on
 * the same component — so component-local state would come back stale.
 *
 * Not persisted: this is server data, an in-memory cache is enough.
 * Notes deliberately live outside this store: they belong to a single screen and
 * a single game, so GameReplay keeps them in local state.
 */
interface ReviewStore {
  folders: ReviewFolder[];
  /** Saved games with no folder — the "Senza cartella" bucket. */
  unsortedCount: number;
  totalSaved: number;
  isLoaded: boolean;
  isLoading: boolean;

  /** No-op once loaded unless `force` — mutations pass force to refresh counts. */
  loadFolders: (force?: boolean) => Promise<void>;
  createFolder: (name: string) => Promise<ReviewFolder>;
  renameFolder: (folderId: string, name: string) => Promise<ReviewFolder>;
  deleteFolder: (folderId: string) => Promise<void>;
  saveGame: (gameId: string, folderId: string | null) => Promise<void>;
  unsaveGame: (gameId: string) => Promise<void>;
  reset: () => void;
}

const EMPTY = { folders: [] as ReviewFolder[], unsortedCount: 0, totalSaved: 0, isLoaded: false, isLoading: false };

export const useReviewStore = create<ReviewStore>((set, get) => ({
  ...EMPTY,

  loadFolders: async (force = false) => {
    if (get().isLoading) return;
    if (get().isLoaded && !force) return;
    set({ isLoading: true });
    try {
      const { folders, unsortedCount, totalSaved } = await fetchFolders();
      set({ folders, unsortedCount, totalSaved, isLoaded: true, isLoading: false });
    } catch (error) {
      // A failed load must not wedge the store: leave isLoaded false so the next
      // caller retries, and let the UI keep rendering without the strip.
      log.warn('Review', 'loadFolders failed', { error: (error as Error)?.message });
      set({ isLoading: false });
    }
  },

  createFolder: async (name: string) => {
    const folder = await apiCreateFolder(name);
    await get().loadFolders(true);
    return folder;
  },

  renameFolder: async (folderId: string, name: string) => {
    const folder = await apiRenameFolder(folderId, name);
    await get().loadFolders(true);
    return folder;
  },

  deleteFolder: async (folderId: string) => {
    await apiDeleteFolder(folderId);
    // The refetch is mandatory, not a nicety: the games move to folder_id NULL
    // (ON DELETE SET NULL), so unsortedCount grows by the deleted folder's
    // gameCount while totalSaved stays put. No hand-patched count gets that right.
    await get().loadFolders(true);
  },

  // After every mutation the whole folder list is refetched (one query, a handful
  // of rows) instead of patching counts by hand — simpler and always correct.
  saveGame: async (gameId: string, folderId: string | null) => {
    await saveGameToFolder(gameId, folderId);
    await get().loadFolders(true);
  },

  unsaveGame: async (gameId: string) => {
    await apiUnsaveGame(gameId);
    await get().loadFolders(true);
  },

  reset: () => set({ ...EMPTY }),
}));
